/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from "react";
import { IServiceEvent } from "../types/IServiceEvent";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { IEventStatus } from "../types/IEventStatus";
import { Card } from "primereact/card";
import { ContextMenu } from "primereact/contextmenu";
import { DeleteServiceStatus } from "../services/DeleteServiceStatus";
import { PostEventStatus } from "../services/PostEventStatus";
import { RetryService } from "../services/RetryService";
import { IEventType } from "../types/IEventType";

const statuses = [
    IEventStatus.UNKNOWN,
    IEventStatus.NEW,
    IEventStatus.PENDING,
    IEventStatus.FAILED,
    IEventStatus.SUCCESS,
    IEventStatus.RESOLVED
];

/**
 * Events table component
 */
export const EventsTable = (props: { id: string; events: IServiceEvent[] }) => {
    const [selectedEvent, setSelectedEvent] = useState<
        IServiceEvent | undefined
    >();

    const cm = useRef<ContextMenu>(null);

    const menuModel = [
        {
            label: "View",
            icon: "pi pi-fw pi-search"
        },
        {
            label: "Retry",
            icon: "pi pi-fw pi-sync",
            command: async () => await retryClick(selectedEvent)
        },
        {
            label: "Resolve",
            icon: "pi pi-fw pi-check",
            command: async () => await resolveClick(selectedEvent)
        },
        {
            label: "Delete",
            icon: "pi pi-fw pi-times",
            command: async () => await deleteClick(selectedEvent)
        }
    ];

    async function deleteClick(event: IServiceEvent | undefined) {
        if (event) {
            const result = await DeleteServiceStatus(props.id, event.eventId);
            console.info(result);
            // location.reload();
        }
    }

    async function retryClick(event: IServiceEvent | undefined) {
        if (event) {
            const result = await RetryService(props.id, event.eventId);
            console.info(result);
            // location.reload();
        }
    }

    const resolveClick = async (event: IServiceEvent | undefined) => {
        if (event) {
            const result = await PostEventStatus(
                event,
                IEventType.SUCCESS,
                "Service marked as resolved",
                IEventStatus.RESOLVED
            );
            console.info(result);
            // location.reload();
        }
    };

    const statusBodyTemplate = (rowData: IServiceEvent) => {
        if (rowData.status == IEventStatus.UNKNOWN) {
            return <Tag className="mr-2" severity="info" value="Unknown"></Tag>;
        }
        if (rowData.status == IEventStatus.NEW) {
            return <Tag className="mr-2" severity="info" value="New"></Tag>;
        }
        if (rowData.status == IEventStatus.PENDING) {
            return (
                <Tag className="mr-2" severity="warning" value="Pending"></Tag>
            );
        }
        if (rowData.status == IEventStatus.FAILED) {
            return (
                <Tag className="mr-2" severity="danger" value="Failed"></Tag>
            );
        }
        if (rowData.status == IEventStatus.SUCCESS) {
            return (
                <Tag className="mr-2" severity="success" value="Success"></Tag>
            );
        }
        if (rowData.status == IEventStatus.RESOLVED) {
            return <Tag className="mr-2" value="Resolved"></Tag>;
        } else {
            return (
                <Tag
                    className="mr-2"
                    severity="warning"
                    value="What is going on?"
                ></Tag>
            );
        }
    };

    const dateBodyTemplate = (rowData: any) => {
        return formatDate(rowData.timestamp);
    };

    const statusItemTemplate = (option: IEventStatus) => {
        let eventStatus;
        if (option == IEventStatus.UNKNOWN) {
            eventStatus = "Unknown";
        }
        if (option == IEventStatus.NEW) {
            eventStatus = "New";
        }
        if (option == IEventStatus.PENDING) {
            eventStatus = "Pending";
        }
        if (option == IEventStatus.FAILED) {
            eventStatus = "Failed";
        }
        if (option == IEventStatus.SUCCESS) {
            eventStatus = "Success";
        }
        if (option == IEventStatus.RESOLVED) {
            eventStatus = "Resolved";
        }
        return (
            <span className={`customer-badge status-${option}`}>
                {eventStatus}
            </span>
        );
    };

    const formatDate = (value: Date) => {
        return value.toUTCString();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statusRowFilterTemplate = (options: any) => {
        return (
            <Dropdown
                value={options.value}
                options={statuses}
                onChange={(e) => options.filterApplyCallback(e.value)}
                itemTemplate={statusItemTemplate}
                placeholder="Select a Status"
                className="p-column-filter"
                showClear
            />
        );
    };
    return (
        <Card
            className="card mt-5"
            title="Recent Events"
            subTitle="All events for this service"
        >
            <DataTable
                value={props.events}
                paginator
                rows={20}
                dataKey="id"
                filterDisplay="row"
                responsiveLayout="scroll"
                emptyMessage="No events found."
                contextMenuSelection={selectedEvent}
                onContextMenuSelectionChange={(e) => setSelectedEvent(e.value)}
                onContextMenu={(e) => {
                    if (cm && cm.current) {
                        cm.current.show(e.originalEvent);
                    }
                }}
            >
                <Column
                    field="eventId"
                    header="Event Id"
                    style={{ minWidth: "250px" }}
                    filterMatchMode="contains"
                    filter
                    filterPlaceholder="Search by ID"
                />

                <Column
                    header="Timestamp"
                    body={dateBodyTemplate}
                    style={{ minWidth: "200px" }}
                />

                <Column
                    field="status"
                    header="Status"
                    showFilterMenu={false}
                    filterMenuStyle={{ width: "100px" }}
                    style={{ minWidth: "250px" }}
                    body={statusBodyTemplate}
                    filter
                    filterElement={statusRowFilterTemplate}
                />

                <Column
                    field="retry"
                    style={{ minWidth: "250px" }}
                    header="Retry Attempts"
                    filter
                    filterPlaceholder="Search by retry"
                />

                <Column
                    field="message"
                    header="Message"
                    style={{ minWidth: "250px" }}
                />

                <Column
                    field="data"
                    header="Event Data"
                    filterMatchMode="contains"
                    filter
                    filterPlaceholder="Search by data content"
                    style={{ minWidth: "300px" }}
                />
            </DataTable>

            <ContextMenu
                model={menuModel}
                ref={cm}
                onHide={() => setSelectedEvent(undefined)}
            />
        </Card>
    );
};
